import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { status } from 'constants/general';
import { fetchRequestOtpInfo, fetchSignWithOtpInfo } from 'modules/accountManagement/actions';
import { loginFormStepsEnum } from 'modules/accountManagement/enums';
import LoginComponent from 'modules/accountManagement/components/LoginComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFieldsWithRegexValidation = {
  phone: /^01[0-9]{9}$/i,
};
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const LoginContainer = ({ isSignUp }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { firebaseConfig } = useSelector(state => ({
    firebaseConfig: state.common.firebaseConfig,
  }));

  const sendAgainTimeout = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [currentStep, setCurrentStep] = useState(loginFormStepsEnum.loginInfo);
  const [formFieldsValues, setFormFieldsValues] = useState({
    phone: '',
    name: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    phone: true,
    name: true,
  });
  const [userAccessError, setUserAccessError] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [otpSeconds, setOtpSeconds] = useState(0);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const addCountDownTimer = () => {
    const countDownSeconds = 30;
    setOtpSeconds(countDownSeconds);
    let seconds = countDownSeconds;

    if (sendAgainTimeout.current) clearInterval(sendAgainTimeout.current);

    sendAgainTimeout.current = setInterval(() => {
      seconds -= 1;

      if (seconds < 0) {
        clearInterval(sendAgainTimeout.current);
      } else setOtpSeconds(prevState => prevState - 1);
    }, 1000);
  };

  useEffect(() => {
    if (currentStep === loginFormStepsEnum.OTP) addCountDownTimer();

    return () => {
      clearInterval(sendAgainTimeout.current);
    };
  }, [currentStep]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const submitUserInformationSuccessCallback = () => {
    setCurrentStep(loginFormStepsEnum.OTP);
  };

  const submitUserOTPFailCallback = () => {
    setOtpError(true);
  };

  const requestUserOtpApiCall = shouldFireCallback => {
    const body = {
      data: {
        CountryCode: '+20',
        FullName: formFieldsValues.name,
        MobileNumber: formFieldsValues.phone,
        SendSMS: true,
        Token: '',
      },
      callbackActionSuccess: (shouldFireCallback && submitUserInformationSuccessCallback) || null,
    };

    dispatch(fetchRequestOtpInfo(body));
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextInputFieldChange = (fieldName, fieldEvent) => {
    const { value } = fieldEvent.target;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;

      return fieldsObj;
    });
  };

  const handleOTPInputFieldChange = fieldValue => {
    setOtpValue(fieldValue);
  };

  const handleChangePhoneBtnClick = () => {
    setCurrentStep(loginFormStepsEnum.loginInfo);
    setFormFieldsValues(prevState => ({
      ...prevState,
      phone: '',
    }));
    setOtpSeconds(0);
    clearInterval(sendAgainTimeout.current);
    setOtpValue('');
    setOtpError(false);
  };

  const handleSendAgainBtnClick = () => {
    addCountDownTimer();
    requestUserOtpApiCall();
  };

  const handleSignUpBtnClick = () => {
    navigate('/signUp');
  };

  const handleSubmitUserInformationBtnClick = () => {
    let isPhoneValid = true;
    let isNameValid = true;
    let isFormValid = true;

    setUserAccessError(false);

    if (!formFieldsValues.phone || !formFieldsValues.name) {
      isPhoneValid = !!formFieldsValues.phone;
      isNameValid = !!formFieldsValues.name;
      isFormValid = false;
    }

    if (requiredFormFieldsWithRegexValidation.phone.test(formFieldsValues.phone) === false) {
      isPhoneValid = false;
      isFormValid = false;
    }

    setFormFieldsValidation({
      phone: isPhoneValid,
      name: isNameValid,
    });

    if (isFormValid === false) return;

    setFormFieldsValidation({
      phone: true,
      name: true,
    });

    if (firebaseConfig.status !== status.SUCCESS) {
      setUserAccessError(true);
      return;
    }

    const userAccessValues = firebaseConfig.details[process.env.REACT_APP_FIREBASE_AUTH_OBJ_KEY];
    const userAccessValuesArr = userAccessValues?.split(',');
    if (!userAccessValuesArr?.includes(formFieldsValues.phone)) {
      setUserAccessError(true);
      return;
    }

    setUserAccessError(false);

    requestUserOtpApiCall(true);
  };

  const handleSubmitUserOtpBtnClick = () => {
    if (otpValue.length !== 6) return;

    setOtpSeconds(0);

    const body = {
      data: {
        CountryCode: '+20',
        Language: 'en-EG',
        MobileNumber: formFieldsValues.phone,
        Token: otpValue,
      },
      callbackActionFail: submitUserOTPFailCallback,
    };

    dispatch(fetchSignWithOtpInfo(body));
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <LoginComponent
      isSignUp={isSignUp}
      currentStep={currentStep}
      formFieldsValues={formFieldsValues}
      formFieldsValidation={formFieldsValidation}
      userAccessError={userAccessError}
      otpValue={otpValue}
      otpError={otpError}
      otpSeconds={otpSeconds}
      handleTextInputFieldChange={handleTextInputFieldChange}
      handleOTPInputFieldChange={handleOTPInputFieldChange}
      handleChangePhoneBtnClick={handleChangePhoneBtnClick}
      handleSendAgainBtnClick={handleSendAgainBtnClick}
      handleSignUpBtnClick={handleSignUpBtnClick}
      handleSubmitUserInformationBtnClick={handleSubmitUserInformationBtnClick}
      handleSubmitUserOtpBtnClick={handleSubmitUserOtpBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

LoginContainer.propTypes = {
  isSignUp: PropTypes.bool,
};

LoginContainer.defaultProps = {
  isSignUp: false,
};

export default LoginContainer;
